import { setContext, setTag, setUser } from '@sentry/nextjs';
import { createContext, useCallback, useContext, useEffect, useState } from 'react';

interface defaultContextType {
  [key: string]: any;
}

// Add type for Sentry user data
interface SentryUserData {
  email?: string;
  id?: string | number;
  hjUserId?: string;
  hjSessionId?: string;
  [key: string]: any;
}

interface SentryContextType {
  mergeContextData: (arg: defaultContextType) => void;
  mergeUserData: (data: Partial<SentryUserData>) => void;
}

const SentryContext = createContext<SentryContextType>({} as any);

interface SentryContextInputType {
  /** String key of tag and context */
  pageName?: string;
}

/**
 * Context for Sentry.
 * Acts as wrapper of Sentry library to provide more detailed information when errors occur
 * by utilizing Tag and Context.
 * Tag and Context is flushed when the component is unmounted.
 * Instead of Sentry.getCurrentScope().clear(), internal state is used to keep other important context such as user information.
 * @see : https://docs.sentry.io/platforms/javascript/enriching-events/
 */
export const SentryProvider: React.FC<React.PropsWithChildren<SentryContextInputType>> = ({
  children,
  pageName,
}) => {
  /** @TODO : might need to use local storage to prevent delay after redirection */
  const [contextData, setContextData] = useState<defaultContextType>({});
  const [, setUserData] = useState<SentryUserData>({});

  const mergeContextData = useCallback((arg) => {
    setContextData((pre) => {
      return {
        ...pre,
        ...arg,
      };
    });
  }, []);

  const mergeUserData = useCallback((newData: Partial<SentryUserData>) => {
    setUserData((prevData) => {
      const mergedData = {
        ...prevData,
        ...newData,
      };

      // Update Sentry whenever the merged data changes
      setUser(Object.keys(mergedData).length > 0 ? mergedData : null);

      return mergedData;
    });
  }, []);

  /**
   * Set up Tag to indicate page in Sentry logs.
   */
  useEffect(() => {
    setTag('page', pageName);
    return () => {
      setTag('page', null);
    };
  }, [pageName]);

  /**
   * Update Context to provide detailed information.
   */
  useEffect(() => {
    setContext(pageName || 'default', contextData);
    return () => {
      setContext(pageName || 'default', null);
    };
  }, [contextData, pageName]);

  return (
    <SentryContext.Provider
      value={{
        mergeContextData,
        mergeUserData,
      }}
    >
      {children}
    </SentryContext.Provider>
  );
};

export const useSentryContext = () => useContext(SentryContext);
